/**
 * WorkflowSpec — the editable shape the Workflow Builder (Studio) authors,
 * either conversationally via the AI agent or by hand on the visual canvas.
 *
 * This is deliberately decoupled from the runtime model in `workflows-v3.ts`:
 *   - A `WorkflowSpec` lives inside a `workflow_drafts` row while being built.
 *   - On "Promote" it materializes a `Workflow` (workflows_v3).
 *   - On "Run" its steps become `WorkflowPlanStep[]` on a `workflow_plans` row.
 *
 * No `zod` in this project, so the validator below is hand-rolled. The AI
 * agent's tool output and every manual edit pass through `validateWorkflowSpec`
 * before being trusted, so it must be defensive (never throw on bad input).
 */

export type WorkflowStepType =
  | 'action'        // an automated PPM action (actionKey required)
  | 'ai'            // an AI-authored artifact (summary, score, draft)
  | 'approval'      // a human approve/reject gate
  | 'notification'  // send a notification and continue
  | 'manual'        // a human performs the work off-platform and checks it off
  | 'gate'          // a policy/condition gate (no work, just routing)
  | 'form'          // render a user-defined Form; its values become run dimensions
  | 'parallel'      // fan out to N approvers with a join rule (all/any/quorum)
  | 'erp_action'    // "Take Action in ERP": a gated SAP write (activity + field map)

/** How an `erp_action` step fires: pause for a human release (default), or run
 *  automatically when reached (only legal when every required param is mapped). */
export type ErpExecutionMode = 'manual' | 'auto'

export type WorkflowTriggerType = 'manual' | 'event' | 'scheduled'

export type WorkflowSpecMode = 'governed' | 'assistive' | 'hybrid'

/**
 * The business-object surface a workflow run is instantiated *within*. A run is
 * always bound to one context so the object's own page can show its in-flight
 * workflows, and the Studio can roll runs up by context. `global` = an ad-hoc
 * run launched from the Studio with no owning object.
 */
export type WorkflowContextType =
  | 'portfolio' | 'project' | 'risk' | 'bcr' | 'request' | 'baseline' | 'forecast'
  | 'cost-transfer' | 'procurement-compliance' | 'opportunity' | 'quote' | 'global'

export const WORKFLOW_CONTEXT_TYPES: WorkflowContextType[] = [
  'portfolio', 'project', 'risk', 'bcr', 'request', 'baseline', 'forecast', 'cost-transfer', 'procurement-compliance', 'opportunity', 'quote', 'global',
]

/** A run's binding to the concrete object it operates on. */
export interface WorkflowContextRef {
  type: WorkflowContextType
  /** Stable id of the object (project id, risk id, BCR id, ...). null for global. */
  id: string | null
  /** Human label shown in run lists and badges, e.g. the project name or risk title. */
  label: string | null
}

export function normalizeContextType(v: unknown): WorkflowContextType {
  const t = typeof v === 'string' ? (v as WorkflowContextType) : 'global'
  return WORKFLOW_CONTEXT_TYPES.includes(t) ? t : 'global'
}

/**
 * A branch out of a step. The engine routes on a branch only when it carries a
 * `match` (the outcome it answers to, see services/workflows/routing.ts):
 *
 *   approved / rejected / returned   an approval, parallel, matrix or
 *                                    department review step was decided
 *   true / false                     a Decision step's condition
 *   case:<value> / default           a Decision step that switches on a field
 *   next                             any step: where to go once it is done
 *
 * Outcomes saved by earlier versions have no `match` and stay unrouted, so a
 * workflow published before branching existed runs as it did.
 */
export interface WorkflowOutcome {
  id: string
  label: string
  /** Next step on this outcome. null/undefined => end the run (completed). */
  toStepId?: string | null
  /** The outcome this branch answers to. Absent on legacy, unrouted outcomes. */
  match?: string
}

/** Routing settings for the whole workflow. */
export interface WorkflowRoutingSettings {
  /** How many times one step may be entered in a run before a loop is stopped.
   *  Blank uses DEFAULT_MAX_STEP_VISITS (services/workflows/routing.ts). */
  maxVisits?: number | null
}

export interface WorkflowStep {
  id: string
  type: WorkflowStepType
  title: string
  description?: string
  /** References an entry in the action registry. Required for action/ai/notification steps. */
  actionKey?: string
  /** For `form` steps: the published form id to collect (its values -> run dimensions). */
  formId?: string | null
  /** Optional map of form field key -> dimension key (identity by default). */
  formFieldMap?: Record<string, string>
  inputs?: Record<string, unknown>
  /** Role or user id responsible (approval/manual steps). */
  assignee?: string | null
  /** Human-readable guard, e.g. "cost delta > $250k". */
  condition?: string | null
  slaHours?: number | null
  /** Role to escalate to when the step breaches its SLA (approval/parallel steps). */
  escalateToRole?: string | null
  // --- erp_action steps (Take Action in ERP) --------------------------------
  /** The SAP_ACTIVITIES id this step executes (execute-mode activity). */
  erpActivityId?: string
  /** Activity param key -> a run dimension path, e.g. { wbsElement: 'form.wbs' }.
   *  Resolved against the run dimensions at execute time. */
  erpFieldMap?: Record<string, string>
  /** Activity param key -> a literal value, used when the value is fixed rather
   *  than sourced from the run/form. Field-map values win over statics. */
  erpStaticValues?: Record<string, string>
  /** manual (default) pauses for a human to release; auto executes when reached. */
  erpExecution?: ErpExecutionMode
  /** Branch outcomes. Empty array => linear flow to the next step in order. */
  outcomes: WorkflowOutcome[]
}

export interface WorkflowTrigger {
  type: WorkflowTriggerType
  /** Business object that fires the trigger, e.g. 'BCR', 'ProjectRequest', 'Risk'. */
  businessObject?: string | null
  /** Event on that object, e.g. 'created', 'submitted', 'threshold_breached'. */
  event?: string | null
  condition?: string | null
  /** "On form submission" trigger: the form id whose submission seeds a run. */
  formId?: string | null
}

export interface WorkflowSpec {
  name: string
  description?: string
  mode: WorkflowSpecMode
  trigger: WorkflowTrigger
  steps: WorkflowStep[]
  notes?: string
  /** Persisted canvas node positions, keyed by node id (step ids plus the
   *  synthetic '__trigger' / '__end' nodes). Set when the user drags a box so
   *  the manual layout survives reload; absent nodes fall back to auto-layout. */
  layout?: Record<string, { x: number; y: number }>
  /** Branch routing settings (loop guard). Absent = defaults. */
  routing?: WorkflowRoutingSettings
}

export type WorkflowDraftStatus = 'draft' | 'in_review' | 'promoted' | 'archived'

export interface WorkflowChatMessage {
  role: 'user' | 'assistant'
  content: string
  /** One-line summary of the canvas change the assistant made on this turn. */
  toolSummary?: string
  ts: string
}

export interface WorkflowChatHistory {
  messages: WorkflowChatMessage[]
}

export interface WorkflowDraft {
  id: string
  name: string
  status: WorkflowDraftStatus
  portfolioId: string | null
  ownerId: string | null
  aiGenerated: boolean
  spec: WorkflowSpec
  chatHistory: WorkflowChatHistory
  promotedWorkflowId: string | null
  createdAt: string
  updatedAt: string
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const WORKFLOW_STEP_TYPES: WorkflowStepType[] = [
  'action', 'ai', 'approval', 'notification', 'manual', 'gate', 'form', 'parallel', 'erp_action',
]
export const WORKFLOW_TRIGGER_TYPES: WorkflowTriggerType[] = ['manual', 'event', 'scheduled']
export const WORKFLOW_SPEC_MODES: WorkflowSpecMode[] = ['governed', 'assistive', 'hybrid']

// ---------------------------------------------------------------------------
// Factory + normalization
// ---------------------------------------------------------------------------

let _idSeq = 0
/** Stable-ish id for canvas steps. Not security-sensitive. */
export function newStepId(): string {
  _idSeq += 1
  return `step_${Date.now().toString(36)}_${_idSeq}`
}

export function emptySpec(name = 'Untitled workflow'): WorkflowSpec {
  return {
    name,
    description: '',
    mode: 'assistive',
    trigger: { type: 'manual', businessObject: null, event: null, condition: null },
    steps: [],
    notes: '',
  }
}

function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback
}

function asOptString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

function asNumberOrNull(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

function normalizeOutcome(raw: unknown, idx: number): WorkflowOutcome {
  const o = (raw ?? {}) as Record<string, unknown>
  return {
    id: asString(o.id) || `out_${idx}`,
    label: asString(o.label) || `Outcome ${idx + 1}`,
    toStepId: asOptString(o.toStepId),
    ...(asOptString(o.match) ? { match: asString(o.match).trim() } : {}),
  }
}

/** Loose spellings the AI builder (or an import) uses for a real step type. */
const STEP_TYPE_SYNONYMS: Record<string, WorkflowStepType> = {
  approve: 'approval',
  approver: 'approval',
  review: 'approval',
  sign_off: 'approval',
  signoff: 'approval',
  human_approval: 'approval',
  single_approval: 'approval',
  approval_gate: 'approval',
  parallel_approval: 'parallel',
  multi_approval: 'parallel',
  notify: 'notification',
  condition: 'gate',
  decision: 'gate',
  branch: 'gate',
  task: 'manual',
  human_task: 'manual',
  erp: 'erp_action',
  erpaction: 'erp_action',
  sap_action: 'erp_action',
  take_action_in_erp: 'erp_action',
}

/**
 * The step type an action key implies. The builder prompt describes each type
 * by its action ("approval ... use action gate.approval"), and the model often
 * answers with type "action" plus that key. The engine and the canvas key off
 * `type`, so such a step showed as a plain Action and never paused for
 * approval.
 */
const ACTION_KEY_STEP_TYPES: Record<string, WorkflowStepType> = {
  'gate.approval': 'approval',
  'gate.parallel_approval': 'parallel',
  'gate.condition': 'gate',
  'gate.branch': 'gate',
  'gate.matrix_review': 'parallel',
  'gate.department_review': 'approval',
  'form.collect': 'form',
  'notify.send': 'notification',
  'sap.deploy': 'erp_action',
}

function normalizeStepType(v: unknown, actionKey?: unknown): WorkflowStepType {
  const t = asString(v).trim().toLowerCase().replace(/[\s-]+/g, '_')
  const known = WORKFLOW_STEP_TYPES.includes(t as WorkflowStepType)
    ? (t as WorkflowStepType)
    : STEP_TYPE_SYNONYMS[t]
  // An explicit non-generic type wins; a generic or unknown one defers to what
  // the action key says the step is.
  if (known && known !== 'action') return known
  const implied = ACTION_KEY_STEP_TYPES[asString(actionKey).trim().toLowerCase()]
  return implied ?? known ?? 'action'
}

function asStringMap(v: unknown): Record<string, string> | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined
  const out: Record<string, string> = {}
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (val === undefined || val === null) continue
    out[k] = String(val)
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function normalizeErpExecution(v: unknown): ErpExecutionMode | undefined {
  return v === 'auto' ? 'auto' : v === 'manual' ? 'manual' : undefined
}

function normalizeStep(raw: unknown, idx: number): WorkflowStep {
  const s = (raw ?? {}) as Record<string, unknown>
  const outcomesRaw = Array.isArray(s.outcomes) ? s.outcomes : []
  return {
    id: asString(s.id) || newStepId(),
    type: normalizeStepType(s.type, s.actionKey),
    title: asString(s.title) || `Step ${idx + 1}`,
    description: asString(s.description) || undefined,
    actionKey: asOptString(s.actionKey) ?? undefined,
    formId: asOptString(s.formId),
    formFieldMap: (s.formFieldMap && typeof s.formFieldMap === 'object') ? (s.formFieldMap as Record<string, string>) : undefined,
    inputs: (s.inputs && typeof s.inputs === 'object') ? (s.inputs as Record<string, unknown>) : {},
    assignee: asOptString(s.assignee),
    condition: asOptString(s.condition),
    slaHours: asNumberOrNull(s.slaHours),
    escalateToRole: asOptString(s.escalateToRole),
    erpActivityId: asOptString(s.erpActivityId) ?? undefined,
    erpFieldMap: asStringMap(s.erpFieldMap),
    erpStaticValues: asStringMap(s.erpStaticValues),
    erpExecution: normalizeErpExecution(s.erpExecution),
    outcomes: outcomesRaw.map(normalizeOutcome),
  }
}

function normalizeTrigger(raw: unknown): WorkflowTrigger {
  const t = (raw ?? {}) as Record<string, unknown>
  const type = asString(t.type) as WorkflowTriggerType
  return {
    type: WORKFLOW_TRIGGER_TYPES.includes(type) ? type : 'manual',
    businessObject: asOptString(t.businessObject),
    event: asOptString(t.event),
    condition: asOptString(t.condition),
    formId: asOptString(t.formId),
  }
}

function normalizeRouting(v: unknown): WorkflowRoutingSettings | undefined {
  if (!v || typeof v !== 'object') return undefined
  const max = asNumberOrNull((v as Record<string, unknown>).maxVisits)
  return max != null && max >= 1 ? { maxVisits: Math.floor(max) } : undefined
}

function normalizeMode(v: unknown): WorkflowSpecMode {
  const m = asString(v) as WorkflowSpecMode
  return WORKFLOW_SPEC_MODES.includes(m) ? m : 'assistive'
}

/**
 * Defensive validator/normalizer. Returns a clean WorkflowSpec on success or a
 * list of human-readable errors. Never throws.
 */
export function validateWorkflowSpec(
  input: unknown,
): { ok: true; spec: WorkflowSpec } | { ok: false; errors: string[] } {
  const errors: string[] = []
  if (!input || typeof input !== 'object') {
    return { ok: false, errors: ['Spec is not an object.'] }
  }
  const raw = input as Record<string, unknown>

  const name = asString(raw.name).trim()
  if (!name) errors.push('Workflow needs a name.')

  const stepsRaw = Array.isArray(raw.steps) ? raw.steps : null
  if (!stepsRaw) errors.push('Spec has no steps array.')

  if (errors.length > 0) return { ok: false, errors }

  const steps = (stepsRaw as unknown[]).map(normalizeStep)

  // Validate outcome references point at real steps (drop dangling ones).
  const stepIds = new Set(steps.map((s) => s.id))
  for (const step of steps) {
    step.outcomes = step.outcomes.filter((o) => !o.toStepId || stepIds.has(o.toStepId))
  }

  // Action/ai/notification steps should carry an actionKey; warn, don't fail.
  // (The builder may legitimately draft a step before its action is mapped.)
  const spec: WorkflowSpec = {
    name,
    description: asString(raw.description) || undefined,
    mode: normalizeMode(raw.mode),
    trigger: normalizeTrigger(raw.trigger),
    steps,
    notes: asString(raw.notes) || undefined,
    layout: (raw.layout && typeof raw.layout === 'object')
      ? (raw.layout as Record<string, { x: number; y: number }>)
      : undefined,
  }
  const routing = normalizeRouting(raw.routing)
  if (routing) spec.routing = routing
  return { ok: true, spec }
}

/** Steps that require a mapped action before the workflow can run. */
export function unmappedActionSteps(spec: WorkflowSpec): WorkflowStep[] {
  return spec.steps.filter(
    (s) => (s.type === 'action' || s.type === 'ai' || s.type === 'notification') && !s.actionKey,
  )
}
